import { createContext, useCallback, useContext, useMemo, useState } from "react";

import { trackEvent } from "@/lib/analytics";

export type WaitlistVariant = "waitlist" | "sales" | "request";

export interface WaitlistMeta {
  role?: string;
  plan?: string;
}

interface WaitlistContextValue {
  open: boolean;
  variant: WaitlistVariant;
  meta: WaitlistMeta;
  openWaitlist: (variant?: WaitlistVariant, meta?: WaitlistMeta) => void;
  close: () => void;
  setOpen: (open: boolean) => void;
}

const Ctx = createContext<WaitlistContextValue | null>(null);

export function WaitlistProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [variant, setVariant] = useState<WaitlistVariant>("waitlist");
  const [meta, setMeta] = useState<WaitlistMeta>({});

  const openWaitlist = useCallback((v: WaitlistVariant = "waitlist", m: WaitlistMeta = {}) => {
    setVariant(v);
    setMeta(m);
    setOpen(true);
    trackEvent("Waitlist_Open", { variant: v });
  }, []);

  const close = useCallback(() => setOpen(false), []);

  const value = useMemo(
    () => ({ open, variant, meta, openWaitlist, close, setOpen }),
    [open, variant, meta, openWaitlist, close],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useWaitlist() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useWaitlist must be used within WaitlistProvider");
  return ctx;
}
